import {RouteLocationNormalized, RouteRecordRaw} from 'vue-router'
import {RouteName} from './enum/RouteName'
import {RouteLayerType} from './enum/RouteLayerType'
import {ClientRouteParamType} from './enum/ClientRouteParamType'
import {ResourceFromRoute} from './types/ResourceFromRoute'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {Pinia} from 'pinia'
import {
  beforeVerifyMailChangeRouterHookFactory,
} from '@toolify/client/src/pages/VerifyMailChangePage/hooks/beforeVerifyMailChangeRouterHook'
import {
  beforeVerifyPasswordChangeRouterHookFactory,
} from '@toolify/client/src/pages/VerifyPasswordChangePage/hooks/beforeVerifyPasswordChangeRouterHook'
import {MdsThumbnailVisualType} from '@toolify/client/src/modules/mds/enum/MdsThumbnailVisualType'
import {
  beforeLinkPageEnterRouterHookFactory,
} from '@toolify/client/src/pages/LinkPage/hooks/beforeLinkPageEnterRouterHook'
import {
  beforeChooseWorkspaceRouterHookFactory,
} from '@toolify/client/src/pages/ChooseWorkspacePage/beforeChooseWorkspaceRouterHook'
import {beforeDatabasePageRouterHookFactory} from '@toolify/client/src/pages/DatabasePage/beforeDatabasePageRouterHook'
import {
  beforeWorkspaceHomeRouterHookFactory,
} from '@toolify/client/src/pages/WorkspacePage/beforeWorkspaceHomeRouterHook'
import {wrapRoutesWithOptionalLocalePrefix} from '@toolify/client/src/setup/router/functions/wrapRoutesWithOptionalLocalePrefix'
import {useDeploymentVersionStore} from '@toolify/client/src/stores/DeploymentVersionStore/useDeploymentVersionStore'
import {
  redirectLegacyResourceTypeQuery,
  resourceCollectionPaths,
} from '@toolify/client/src/modules/resources/functions/getResourceCollectionRoute'
import {isNotificationsPageEnabled} from '@toolify/client/src/modules/notifications/constants/isNotificationsPageEnabled'

export function setupClientRoutes(pinia: Pinia): RouteRecordRaw[] {
  return wrapRoutesWithOptionalLocalePrefix([
    {
      path: '/share/:linkId',
      component: () => import('../../pages/LinkPage/LinkPage.vue'),
      beforeEnter: beforeLinkPageEnterRouterHookFactory(pinia),
    },
    {
      path: '/share/:linkId/item/:databaseItemId/file/:databaseItemFileId',
      component: () => import('../../views/FilePreviewView.vue'),
      meta: {
        depth: 150,
        isStackable: true,
        isFilePreview: true,
      },
      children: [
        {
          path: '',
          name: RouteName.ShareLinkDatabaseItemFile,
          component: () => import('../../pages/DatabaseFilePage/DatabaseFilePage.vue'),
        },
      ],
    },
    {
      path: '/android',
      name: RouteName.Android,
      component: () => import('../../pages/AndroidPage/AndroidPage.vue'),
    },
    {
      path: '/workspaces',
      component: () => import('../../views/ChooseWorkspaceView.vue'),
      meta: {
        depth: 200,
        auth: {
          required: true,
          value: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.ChooseWorkspace,
          component: () => import('../../pages/ChooseWorkspacePage/ChooseWorkspacePage.vue'),
          beforeEnter: beforeChooseWorkspaceRouterHookFactory(pinia),
          meta: {
            auth: {
              required: true,
              value: true,
            },
          },
        },
      ],
    },
    {
      path: '/onboarding',
      component: () => import('../../views/OnboardingView.vue'),
      meta: {
        depth: 200,
        auth: {
          required: true,
          value: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.Onboarding,
          component: () => import('../../modules/onboarding/pages/OnboardingPage/OnboardingPage.vue'),
          meta: {
            auth: {
              required: true,
              value: true,
            },
          },
        },
      ],
    },
    {
      path: '',
      component: () => import('../../views/AuthenticationView.vue'),
      meta: {
        depth: 200,
        auth: {
          required: true,
          value: false,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.Login,
          component: () => import('../../pages/LoginPage/LoginPage.vue'),
          meta: {
            onboarding: {
              graphicTitleKey: null,
              graphicVisual: MdsThumbnailVisualType.Padlock,
              graphicSceneTitleKey: 'authGraphic.login.title',
              graphicSceneSubtitleKey: 'authGraphic.login.subtitle',
            },
          },
        },
        {
          path: 'join',
          name: RouteName.Register,
          component: () => import('../../pages/RegisterPage/RegisterPage.vue'),
          meta: {
            onboarding: {
              graphicSceneTitleKey: 'authGraphic.register.title',
              graphicSceneSubtitleKey: 'authGraphic.register.subtitle',
            },
          },
        },
        {
          path: 'verify-mail-address/:verificationId',
          name: RouteName.VerifyMailAddress,
          component: () => import('../../pages/VerifyMailAddressPage/VerifyMailAddressPage.vue'),
          meta: {
            onboarding: {
              graphicSceneTitleKey: 'authGraphic.verifyMail.title',
              graphicSceneSubtitleKey: 'authGraphic.verifyMail.subtitle',
            },
          },
        },
        {
          path: 'verify-mail-change/:verificationId',
          name: RouteName.VerifyMailChange,
          component: () => import('../../pages/VerifyMailChangePage/VerifyMailChangePage.vue'),
          beforeEnter: beforeVerifyMailChangeRouterHookFactory(pinia),
          meta: {
            auth: {
              required: false,
              value: false,
            },
            onboarding: {
              graphicSceneTitleKey: 'authGraphic.verifyMailChange.title',
              graphicSceneSubtitleKey: 'authGraphic.verifyMailChange.subtitle',
            },
          },
        },
        {
          path: 'verify-password-change/:verificationId',
          name: RouteName.VerifyPasswordChange,
          component: () => import('../../pages/VerifyPasswordChangePage/VerifyPasswordChangePage.vue'),
          beforeEnter: beforeVerifyPasswordChangeRouterHookFactory(pinia),
          meta: {
            auth: {
              required: false,
              value: false,
            },
            onboarding: {
              graphicSceneTitleKey: 'authGraphic.verifyPasswordChange.title',
              graphicSceneSubtitleKey: 'authGraphic.verifyPasswordChange.subtitle',
            },
          },
        },
        {
          path: 'reset-password/:verificationToken',
          name: RouteName.ResetPassword,
          redirect: to => ({
            name: RouteName.ForgotPassword,
            params: to.params,
            query: to.query,
          }),
        },
        {
          path: 'forgot-password/:verificationToken?',
          name: RouteName.ForgotPassword,
          component: () => import('../../pages/ForgotPasswordPage/ForgotPasswordPage.vue'),
          meta: {
            onboarding: {
              graphicTitleKey: null,
              graphicVisual: MdsThumbnailVisualType.Padlock,
              graphicSceneTitleKey: 'authGraphic.forgotPassword.title',
              graphicSceneSubtitleKey: 'authGraphic.forgotPassword.subtitle',
            },
          },
        },
        {
          path: 'invitation/:invitationId-:invitationSecret',
          name: RouteName.Invitation,
          component: () => import('../../pages/WorkspaceInvitationPage/WorkspaceInvitationPage.vue'),
          meta: {
            auth: {
              required: false,
              value: false,
              targetRouteName: RouteName.Register,
            },
            onboarding: {
              graphicSceneTitleKey: 'authGraphic.invitation.title',
              graphicSceneSubtitleKey: 'authGraphic.invitation.subtitle',
            },
          },
        },
        {
          path: 'create-workspace',
          name: RouteName.CreateWorkspace,
          component: () => import('../../pages/CreateWorkspacePage/CreateWorkspacePage.vue'),
          meta: {
            auth: {
              required: true,
              value: true,
            },
            onboarding: {
              graphicTitleKey: 'onboarding.graphic.headlines.workspace',
              graphicVisual: MdsThumbnailVisualType.Table,
              graphicSceneTitleKey: 'authGraphic.createWorkspace.title',
              graphicSceneSubtitleKey: 'authGraphic.createWorkspace.subtitle',
            },
          },
        },
      ],
    },
    {
      path: '/auth/identity/:provider/callback',
      name: RouteName.OAuthIdentityCallback,
      component: () => import('../../pages/OAuthIdentityCallbackPage/OAuthIdentityCallbackPage.vue'),
      meta: {
        depth: 200,
        auth: {
          required: false,
          value: false,
        },
      },
    },
    {
      path: '/oauth/app/consent',
      name: RouteName.OAuthAppConsent,
      component: () => import('../../pages/OAuthAppConsentPage/OAuthAppConsentPage.vue'),
      meta: {
        depth: 200,
        auth: {
          required: true,
          value: true,
        },
      },
    },
    {
      path: '/auth/connection/callback',
      name: RouteName.ConnectionAuthCallback,
      component: () => import('../../pages/ConnectionAuthCallbackPage/ConnectionAuthCallbackPage.vue'),
      meta: {
        depth: 200,
        auth: {
          required: true,
          value: true,
        },
      },
    },
    {
      path: '/chat',
      component: () => import('../../views/ChatView.vue'),
      meta: {
        routeLayer: RouteLayerType.App,
        depth: 100,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.NewDirectConversation,
          component: () => import('../../pages/NewChatPage/NewChatPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: ':directConversationId',
          name: RouteName.DirectConversation,
          components: {
            default: () => import('../../pages/ChatPage/ChatPage.vue'),
            stackable: () => import('../../pages/StackableDirectConversationPage/StackableDirectConversationPage.vue'),
          },
          meta: {
            order: 0,
            isStackable: true,
            isStackableCondition: (to: RouteLocationNormalized) => to.query.stackable === 'true',
            determineResource(route): ResourceFromRoute | null {
              const resourceId = route.params[ClientRouteParamType.DirectConversationId] as string | undefined
              return resourceId ? {
                resourceType: ResourceType.DirectConversation,
                resourceId,
              } : null
            },
          },
        },
      ],
    },
    {
      path: '/chat/:directConversationId/message/:messageId/file/:fileId',
      component: () => import('../../views/FilePreviewView.vue'),
      meta: {
        auth: {
          required: true,
        },
        depth: 150,
        isStackable: true,
        isFilePreview: true,
      },
      children: [
        {
          path: '',
          name: RouteName.DirectConversationFile,
          component: () => import('../../pages/DirectConversationFilePage/DirectConversationFilePage.vue'),
        },
      ],
    },
    {
      path: '/support',
      component: () => import('../../layouts/SupportHubLayout.vue'),
      meta: {
        routeLayer: RouteLayerType.Support,
        depth: 150,
        auth: {
          required: false,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.Support,
          component: () => import('../../pages/SupportPage/SupportPage.vue'),
        },
      ],
    },
    {
      path: '/tickets',
      component: () => import('../../layouts/SupportHubLayout.vue'),
      meta: {
        routeLayer: RouteLayerType.Support,
        depth: 150,
        auth: {
          required: false,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.SupportTickets,
          component: () => import('../../pages/MyTicketsPage/MyTicketsPage.vue'),
        },
      ],
    },
    {
      path: '/changelog',
      component: () => import('../../layouts/SupportHubLayout.vue'),
      meta: {
        routeLayer: RouteLayerType.Support,
        depth: 150,
        auth: {
          required: false,
        },
        canLeaveRoute: (hookPinia) => !useDeploymentVersionStore(hookPinia).mustUpdate,
      },
      children: [
        {
          path: '',
          name: RouteName.Changelog,
          component: () => import('../../pages/ChangelogPage/ChangelogPage.vue'),
          meta: {
            canLeaveRoute: (hookPinia) => !useDeploymentVersionStore(hookPinia).mustUpdate,
          },
        },
      ],
    },
    {
      path: '/upcoming-features',
      component: () => import('../../layouts/SupportHubLayout.vue'),
      meta: {
        routeLayer: RouteLayerType.Support,
        depth: 150,
        auth: {
          required: false,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.UpcomingFeatures,
          component: () => import('../../pages/UpcomingFeaturesPage/UpcomingFeaturesPage.vue'),
        },
      ],
    },
    {
      path: '/@:workspaceName',
      component: () => import('../../views/WorkspaceView.vue'),
      meta: {
        requireWorkspace: true,
        auth: {
          required: true,
        },
        routeLayer: RouteLayerType.App,
        depth: 100,
      },
      children: [
        {
          path: '',
          name: RouteName.WorkspaceHome,
          component: () => import('@toolify/client/src/pages/WorkspacePage/WorkspacePage.vue'),
          beforeEnter: [
            beforeWorkspaceHomeRouterHookFactory(pinia),
          ],
        },
        {
          path: 'notifications',
          component: () => import('../../views/NotificationsView.vue'),
          meta: {
            depth: 150,
            requireSuperAdmin: true,
          },
          beforeEnter: (to) => isNotificationsPageEnabled || {
            name: RouteName.WorkspaceHome,
            params: {
              workspaceName: to.params.workspaceName,
            },
          },
          children: [
            {
              path: '',
              name: RouteName.Notifications,
              component: () => import('../../pages/NotificationsPage/NotificationsPage.vue'),
              meta: {
                order: 0,
              },
            },
            {
              path: ':notificationId',
              name: RouteName.Notification,
              component: () => import('../../pages/NotificationPage/NotificationPage.vue'),
              meta: {
                order: 1,
              },
            },
          ],
        },
        {
          path: 'apps/manage',
          name: RouteName.Apps,
          component: () => import('@toolify/client/src/pages/AppsPage/AppsPage.vue'),
        },
        {
          path: 'crons/manage',
          name: RouteName.Crons,
          component: () => import('@toolify/client/src/pages/CronsPage/CronsPage.vue'),
        },
        {
          path: 'connections/manage',
          name: RouteName.Connections,
          component: () => import('@toolify/client/src/pages/ConnectionsPage/ConnectionsPage.vue'),
        },
        {
          path: 'resources',
          name: RouteName.WorkspaceResources,
          component: () => import('@toolify/client/src/pages/ResourcesPage/ResourcesPage.vue'),
          beforeEnter: redirectLegacyResourceTypeQuery,
        },
        {
          path: `:${ClientRouteParamType.ResourceCollection}(${resourceCollectionPaths.join('|')})`,
          name: RouteName.WorkspaceResourceCollection,
          component: () => import('@toolify/client/src/pages/ResourcesPage/ResourcesPage.vue'),
        },
        {
          path: 'collections',
          name: RouteName.Collections,
          component: () => import('@toolify/client/src/pages/CollectionPage/CollectionPage.vue'),
        },
        {
          path: `collections/:${ClientRouteParamType.CollectionItemId}`,
          name: RouteName.Collection,
          component: () => import('@toolify/client/src/pages/CollectionPage/CollectionPage.vue'),
        },
        {
          path: 'recents',
          name: RouteName.CollectionRecents,
          component: () => import('@toolify/client/src/pages/CollectionPage/CollectionPage.vue'),
        },
        {
          path: 'connection/:connectionId',
          name: RouteName.Connection,
          component: () => import('@toolify/client/src/pages/ConnectionPage/ConnectionPage.vue'),
        },
        {
          path: 'webhook-listeners',
          name: RouteName.WebhookListeners,
          component: () => import('@toolify/client/src/pages/WebhookListenersPage/WebhookListenersPage.vue'),
        },
        {
          path: 'app/:appId',
          component: () => import('@toolify/client/src/pages/AppPage/AppPageLayout.vue'),
          children: [
            {
              path: '',
              name: RouteName.AppApplication,
              component: () => import('@toolify/client/src/pages/AppPage/AppPage.vue'),
            },
            {
              path: 'items',
              name: RouteName.AppItems,
              component: () => import('@toolify/client/src/pages/AppPage/AppItemsPage.vue'),
            },
            {
              path: 'connections',
              name: RouteName.AppConnections,
              component: () => import('@toolify/client/src/pages/AppPage/AppConnectionsPage.vue'),
            },
            {
              path: 'webhooks',
              name: RouteName.AppWebhooks,
              component: () => import('@toolify/client/src/pages/AppPage/AppWebhooksPage.vue'),
            },
            {
              path: 'dynamic-fields',
              name: RouteName.AppDynamicFields,
              component: () => import('@toolify/client/src/pages/AppPage/AppDynamicFieldsPage.vue'),
            },
            {
              path: 'code',
              name: RouteName.AppCode,
              component: () => import('@toolify/client/src/pages/AppPage/AppCodePage.vue'),
            },
            {
              path: 'api-keys',
              name: RouteName.AppApiKeys,
              component: () => import('@toolify/client/src/pages/AppPage/AppApiKeysPage.vue'),
            },

          ],
        },
        {
          path: 'database/:databaseId',
          meta: {
            determineResource(route): ResourceFromRoute | null {
              const resourceId = route.params[ClientRouteParamType.DatabaseId] as string | undefined
              return resourceId ? {
                resourceType: ResourceType.Database,
                resourceId,
              } : null
            },
          },
          children: [
            {
              path: '',
              name: RouteName.Database,
              component: () => import('../../pages/DatabasePage/DatabasePage.vue'),
              beforeEnter: beforeDatabasePageRouterHookFactory(pinia),
            },
            {
              path: 'v/:databaseViewId?',
              name: RouteName.DatabaseView,
              component: () => import('../../pages/DatabaseViewPage/DatabaseViewPage.vue'),
              // beforeEnter: beforeDatabaseViewPageRouterHookFactory(pinia),
            },
            // {
            //   path: 's/:databaseSliceId',
            //   children: [
            //     {
            //       path: '',
            //       name: RouteName.DatabaseSlice,
            //       component: () => import('../../pages/DatabaseSlicePage/DatabaseSlicePage.vue'),
            //       beforeEnter: beforeDatabaseSlicePageRouterHookFactory(pinia),
            //       //on reload
            //     },
            //     {
            //       path: 'v/:databaseViewId',
            //       name: RouteName.DatabaseView,
            //       component: () => import('../../pages/DatabaseViewPage/DatabaseViewPage.vue'),
            //     },
            //   ],
            // },
            {
              path: 'item/:databaseItemId',
              name: RouteName.DatabaseItem,
              components: {
                default: () => import('../../pages/DatabaseItemPage/DatabaseItemPage.vue'),
                stackable: () => import('../../pages/StackableDatabaseItemPage/StackableDatabaseItemLayerPage.vue'),
              },
              meta: {
                isStackable: true,
              },
            },
            // {
            //   path: 's/:databaseSliceId/v/:databaseViewId',
            //   name: RouteName.DatabaseView,
            //   component: () => import('../../pages/DatabasePage/DatabasePage.vue'),
            // },

          ],
        },
        {
          path: 'document/:documentId',
          name: RouteName.Document,
          component: () => import('../../pages/DocumentPage/DocumentPage.vue'),
          meta: {
            determineResource(route): ResourceFromRoute | null {
              const resourceId = route.params[ClientRouteParamType.DocumentId] as string | undefined
              return resourceId ? {
                resourceType: ResourceType.Document,
                resourceId,
              } : null
            },
          },
        },
        {
          path: 'whiteboard/:whiteboardId',
          name: RouteName.Whiteboard,
          component: () => import('../../pages/WhiteboardPage/WhiteboardPage.vue'),
          meta: {
            determineResource(route): ResourceFromRoute | null {
              const resourceId = route.params[ClientRouteParamType.WhiteboardId] as string | undefined
              return resourceId ? {
                resourceType: ResourceType.Whiteboard,
                resourceId,
              } : null
            },
          },
        },
        {
          path: 'skill/:skillId',
          name: RouteName.Skill,
          component: () => import('../../pages/SkillPage/SkillPage.vue'),
          meta: {
            determineResource(route): ResourceFromRoute | null {
              const resourceId = route.params[ClientRouteParamType.SkillId] as string | undefined
              return resourceId ? {
                resourceType: ResourceType.Skill,
                resourceId,
              } : null
            },
          },
        },
        {
          path: 'agent/:agentId',
          name: RouteName.Agent,
          component: () => import('../../pages/AgentPage/AgentPage.vue'),
          meta: {
            determineResource(route): ResourceFromRoute | null {
              const resourceId = route.params[ClientRouteParamType.AgentId] as string | undefined
              return resourceId ? {
                resourceType: ResourceType.Agent,
                resourceId,
              } : null
            },
          },
        },
        {
          path: 'templates',
          name: RouteName.WorkspaceTemplates,
          component: () => import('../../pages/WorkspaceTemplatesPage/WorkspaceTemplatesPage.vue'),
        },
        {
          path: 'conversation/:conversationId',
          name: RouteName.Conversation,
          components: {
            default: () => import('../../pages/ConversationPage/ConversationPage.vue'),
            stackable: () => import('../../pages/StackableConversationPage/StackableConversationPage.vue'),
          },
          meta: {
            isStackable: true,
            isStackableCondition: (to: RouteLocationNormalized) => to.query.stackable === 'true',
            determineResource(route): ResourceFromRoute | null {
              const resourceId = route.params[ClientRouteParamType.ConversationId] as string | undefined
              return resourceId ? {
                resourceType: ResourceType.Conversation,
                resourceId,
              } : null
            },
          },
        },
        {
          path: 'dashboard/:dashboardId',
          name: RouteName.Dashboard,
          component: () => import('../../pages/DashboardPage/DashboardPage.vue'),
          meta: {
            determineResource(route): ResourceFromRoute | null {
              const resourceId = route.params[ClientRouteParamType.DashboardId] as string | undefined
              return resourceId ? {
                resourceType: ResourceType.Dashboard,
                resourceId,
              } : null
            },
          },
        },
        {
          path: 'folder/:folderId',
          name: RouteName.Folder,
          component: () => import('../../pages/FolderPage/FolderPage.vue'),
          meta: {
            determineResource(route): ResourceFromRoute | null {
              const resourceId = route.params[ClientRouteParamType.FolderId] as string | undefined
              return resourceId ? {
                resourceType: ResourceType.Folder,
                resourceId,
              } : null
            },
          },
        },
        {
          path: 'folder/:folderId/catalog/:catalogCloudItemId',
          name: RouteName.CloudCatalog,
          component: () => import('../../pages/CloudItemCatalogPage/CloudItemCatalogPage.vue'),
        },
        {
          path: 'automation/:automationId',
          name: RouteName.Automation,
          component: () => import('../../pages/AutomationPage/AutomationPage.vue'),
          meta: {
            determineResource(route): ResourceFromRoute | null {
              const resourceId = route.params[ClientRouteParamType.AutomationId] as string | undefined
              return resourceId ? {
                resourceType: ResourceType.Automation,
                resourceId,
              } : null
            },
          },
        },
        {
          path: 'automation/:automationId/run/:automationRunId',
          name: RouteName.AutomationRunDetails,
          component: () => import('../../pages/AutomationRunDetailsPage/AutomationRunDetailsPage.vue'),
        },
        {
          path: 'call-room/:callRoomId',
          name: RouteName.CallRoom,
          component: () => import('../../pages/CallPage/CallRoomPage.vue'),
          meta: {
            determineResource(route): ResourceFromRoute | null {
              const resourceId = route.params.callRoomId as string | undefined
              return resourceId ? {
                resourceType: ResourceType.CallRoom,
                resourceId,
              } : null
            },
          },
        },
      ],
    },
    {
      path: '/@:workspaceName/file/:fileCloudItemId',
      component: () => import('../../views/FilePreviewView.vue'),
      meta: {
        requireWorkspace: true,
        auth: {
          required: true,
        },
        depth: 150,
        isStackable: true,
        isFilePreview: true,
      },
      children: [
        {
          path: '',
          name: RouteName.CloudFile,
          component: () => import('../../pages/CloudItemFilePage/CloudItemFilePage.vue'),
        },
      ],
    },
    {
      path: '/@:workspaceName/database/:databaseId/item/:databaseItemId/file/:databaseItemFileId',
      component: () => import('../../views/FilePreviewView.vue'),
      meta: {
        requireWorkspace: true,
        auth: {
          required: true,
        },
        depth: 150,
        isStackable: true,
        isFilePreview: true,
      },
      children: [
        {
          path: '',
          name: RouteName.DatabaseItemFile,
          component: () => import('../../pages/DatabaseFilePage/DatabaseFilePage.vue'),
        },
      ],
    },
    {
      path: '/@:workspaceName/database/:databaseId/item/:databaseItemId/message/:messageId/file/:fileId',
      component: () => import('../../views/FilePreviewView.vue'),
      meta: {
        requireWorkspace: true,
        auth: {
          required: true,
        },
        depth: 150,
        isStackable: true,
        isFilePreview: true,
      },
      children: [
        {
          path: '',
          name: RouteName.DatabaseCellCommentFile,
          component: () => import('../../pages/DatabaseCellCommentFile/DatabaseCellCommentFilePage.vue'),
        },
      ],
    },
    {
      path: '/@:workspaceName/database/:databaseId/item/:databaseItemId/file/:databaseItemFileId/message/:messageId/file/:fileId',
      component: () => import('../../views/FilePreviewView.vue'),
      meta: {
        requireWorkspace: true,
        auth: {
          required: true,
        },
        depth: 150,
        isStackable: true,
        isFilePreview: true,
      },
      children: [
        {
          path: '',
          name: RouteName.DatabaseItemFileCommentFile,
          component: () => import('../../pages/DatabaseItemFileCommentFilePage/DatabaseItemFileCommentFilePage.vue'),
        },
      ],
    },
    {
      path: '/@:workspaceName/file/:cloudItemId/message/:messageId/file/:fileId',
      component: () => import('../../views/FilePreviewView.vue'),
      meta: {
        requireWorkspace: true,
        auth: {
          required: true,
        },
        depth: 150,
        isStackable: true,
        isFilePreview: true,
      },
      children: [
        {
          path: '',
          name: RouteName.CloudItemCommentFile,
          component: () => import('../../pages/CloudItemCommentFilePage/CloudItemCommentFilePage.vue'),
        },
      ],
    },
    {
      path: '/@:workspaceName/message/:messageId/file/:fileId',
      component: () => import('../../views/FilePreviewView.vue'),
      meta: {
        requireWorkspace: true,
        auth: {
          required: true,
        },
        depth: 150,
        isStackable: true,
        isFilePreview: true,
      },
      children: [
        {
          path: '',
          name: RouteName.ConversationFile,
          component: () => import('../../pages/ConversationFile/ConversationFilePage.vue'),
        },
      ],
    },
    {
      path: '/@:workspaceName/database/:databaseId/settings',
      component: () => import('../../views/DatabaseSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.DatabaseSettings,
          component: () => import('../../pages/DatabaseSettings/DatabaseSettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: 'notifications',
          name: RouteName.DatabaseNotificationSettings,
          component: () => import('../../pages/DatabaseSettings/DatabaseNotificationSettingsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'watchers',
          name: RouteName.DatabaseWatcherSettings,
          component: () => import('../../pages/DatabaseSettings/DatabaseWatcherSettingsPage.vue'),
          meta: {
            order: 2,
          },
        },
        {
          path: 'permissions',
          name: RouteName.DatabasePermissions,
          component: () => import('../../pages/DatabaseSettings/DatabasePermissionsPage.vue'),
          meta: {
            order: 3,
          },
        },
        {
          path: 'webhooks',
          name: RouteName.DatabaseWebhooks,
          component: () => import('../../pages/DatabaseSettings/DatabaseWebhooksPage.vue'),
          meta: {
            order: 5,
          },
        },
        {
          path: 'share',
          name: RouteName.DatabaseLinks,
          component: () => import('../../pages/DatabaseSettings/DatabaseLinksPage.vue'),
          meta: {
            order: 6,
          },
        },
        {
          path: 'activity',
          name: RouteName.DatabaseActivityLog,
          component: () => import('../../pages/DatabaseSettings/DatabaseActivityLogPage.vue'),
          meta: {
            order: 7,
          },
        },
        {
          path: 'trash',
          name: RouteName.DatabaseTrash,
          component: () => import('../../pages/DatabaseSettings/DatabaseTrashPage.vue'),
          meta: {
            order: 8,
          },
        },
      ],
    },
    {
      path: '/@:workspaceName/skill/:skillId/settings',
      component: () => import('../../views/SkillSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.SkillSettings,
          redirect: to => ({
            name: RouteName.SkillPermissions,
            params: to.params,
          }),
        },
        {
          path: 'permissions',
          name: RouteName.SkillPermissions,
          component: () => import('../../pages/SkillSettings/SkillPermissionsPage.vue'),
          meta: {order: 0},
        },
      ],
    },
    {
      path: '/@:workspaceName/agent/:agentId/settings',
      component: () => import('../../views/AgentSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.AgentSettings,
          component: () => import('../../pages/AgentSettings/AgentSettingsPage.vue'),
          meta: {order: 0},
        },
        {
          path: 'permissions',
          name: RouteName.AgentPermissions,
          component: () => import('../../pages/AgentSettings/AgentPermissionsPage.vue'),
          meta: {order: 1},
        },
        {
          path: 'notifications',
          name: RouteName.AgentNotificationSettings,
          component: () => import('../../pages/AgentSettings/AgentNotificationSettingsPage.vue'),
          meta: {order: 2},
        },
        {
          path: 'watchers',
          name: RouteName.AgentWatcherSettings,
          component: () => import('../../pages/AgentSettings/AgentWatcherSettingsPage.vue'),
          meta: {order: 3},
        },
        {
          path: 'webhooks',
          name: RouteName.AgentWebhooks,
          component: () => import('../../pages/AgentSettings/AgentWebhooksPage.vue'),
          meta: {order: 4},
        },
        {
          path: 'activity',
          name: RouteName.AgentActivityLog,
          component: () => import('../../pages/AgentSettings/AgentActivityLogPage.vue'),
          meta: {order: 5},
        },
      ],
    },
    {
      path: '/@:workspaceName/document/:documentId/settings',
      component: () => import('../../views/DocumentSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.DocumentSettings,
          component: () => import('../../pages/DocumentSettings/DocumentSettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: 'notifications',
          name: RouteName.DocumentNotificationSettings,
          component: () => import('../../pages/DocumentSettings/DocumentNotificationSettingsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'watchers',
          name: RouteName.DocumentWatcherSettings,
          component: () => import('../../pages/DocumentSettings/DocumentWatcherSettingsPage.vue'),
          meta: {
            order: 2,
          },
        },
        {
          path: 'permissions',
          name: RouteName.DocumentPermissions,
          component: () => import('../../pages/DocumentSettings/DocumentPermissionsPage.vue'),
          meta: {
            order: 3,
          },
        },
        {
          path: 'webhooks',
          name: RouteName.DocumentWebhooks,
          component: () => import('../../pages/DocumentSettings/DocumentWebhooksPage.vue'),
          meta: {
            order: 4,
          },
        },
        {
          path: 'activity',
          name: RouteName.DocumentActivityLog,
          component: () => import('../../pages/DocumentSettings/DocumentActivityLogPage.vue'),
          meta: {
            order: 5,
          },
        },
      ],
    },
    {
      path: '/@:workspaceName/whiteboard/:whiteboardId/settings',
      component: () => import('../../views/WhiteboardSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.WhiteboardSettings,
          component: () => import('../../pages/WhiteboardSettings/WhiteboardSettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: 'notifications',
          name: RouteName.WhiteboardNotificationSettings,
          component: () => import('../../pages/WhiteboardSettings/WhiteboardNotificationSettingsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'watchers',
          name: RouteName.WhiteboardWatcherSettings,
          component: () => import('../../pages/WhiteboardSettings/WhiteboardWatcherSettingsPage.vue'),
          meta: {
            order: 2,
          },
        },
        {
          path: 'permissions',
          name: RouteName.WhiteboardPermissions,
          component: () => import('../../pages/WhiteboardSettings/WhiteboardPermissionsPage.vue'),
          meta: {
            order: 3,
          },
        },
        {
          path: 'webhooks',
          name: RouteName.WhiteboardWebhooks,
          component: () => import('../../pages/WhiteboardSettings/WhiteboardWebhooksPage.vue'),
          meta: {
            order: 4,
          },
        },
        {
          path: 'activity',
          name: RouteName.WhiteboardActivityLog,
          component: () => import('../../pages/WhiteboardSettings/WhiteboardActivityLogPage.vue'),
          meta: {
            order: 5,
          },
        },
      ],
    },
    {
      path: '/chat/:directConversationId/settings',
      component: () => import('../../views/DirectConversationSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.DirectConversationSettings,
          component: () => import('../../pages/ChatPage/DirectConversationSettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
      ],
    },
    {
      path: '/@:workspaceName/conversation/:conversationId/settings',
      component: () => import('../../views/ConversationSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.ConversationSettings,
          component: () => import('../../pages/ConversationSettings/ConversationSettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: 'notifications',
          name: RouteName.ConversationNotificationSettings,
          component: () => import('../../pages/ConversationSettings/ConversationNotificationSettingsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'watchers',
          name: RouteName.ConversationWatcherSettings,
          component: () => import('../../pages/ConversationSettings/ConversationWatcherSettingsPage.vue'),
          meta: {
            order: 2,
          },
        },
        {
          path: 'permissions',
          name: RouteName.ConversationPermissions,
          component: () => import('../../pages/ConversationSettings/ConversationPermissionsPage.vue'),
          meta: {
            order: 3,
          },
        },
      ],
    },
    {
      path: '/@:workspaceName/dashboard/:dashboardId/settings',
      component: () => import('../../views/DashboardSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.DashboardSettings,
          component: () => import('../../pages/DashboardSettings/DashboardSettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: 'permissions',
          name: RouteName.DashboardPermissions,
          component: () => import('../../pages/DashboardSettings/DashboardPermissionsPage.vue'),
          meta: {
            order: 1,
          },
        },
      ],
    },
    {
      path: '/@:workspaceName/folder/:folderId/settings',
      component: () => import('../../views/FolderSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.FolderSettings,
          component: () => import('../../pages/FolderSettings/FolderSettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: 'notifications',
          name: RouteName.FolderNotificationSettings,
          component: () => import('../../pages/FolderSettings/FolderNotificationSettingsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'watchers',
          name: RouteName.FolderWatcherSettings,
          component: () => import('../../pages/FolderSettings/FolderWatcherSettingsPage.vue'),
          meta: {
            order: 2,
          },
        },
        {
          path: 'permissions',
          name: RouteName.FolderPermissions,
          component: () => import('../../pages/FolderSettings/FolderPermissionsPage.vue'),
          meta: {
            order: 3,
          },
        },
        {
          path: 'webhooks',
          name: RouteName.FolderWebhooks,
          component: () => import('../../pages/FolderSettings/FolderWebhooksPage.vue'),
          meta: {
            order: 4,
          },
        },
      ],
    },
    {
      path: '/@:workspaceName/automation/:automationId/settings',
      component: () => import('../../views/AutomationSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.AutomationSettings,
          component: () => import('../../pages/AutomationSettings/AutomationSettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: 'permissions',
          name: RouteName.AutomationPermissions,
          component: () => import('../../pages/AutomationSettings/AutomationPermissionsPage.vue'),
          meta: {
            order: 1,
          },
        },
      ],
    },
    {
      path: '/@:workspaceName/call/:callRoomId/settings',
      component: () => import('../../views/CallSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.CallSettings,
          component: () => import('../../pages/CallSettings/CallSettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: 'permissions',
          name: RouteName.CallPermissions,
          component: () => import('../../pages/CallSettings/CallPermissionsPage.vue'),
          meta: {
            order: 1,
          },
        },
      ],
    },
    {
      path: '/@:workspaceName/database/:databaseId/v/:databaseViewId?',
      component: () => import('../../views/DatabaseViewSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: 'settings',
          name: RouteName.DatabaseViewSettings,
          component: () => import('../../pages/DatabaseViewSettings/DatabaseViewSettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: 'permissions',
          name: RouteName.DatabaseViewPermissions,
          component: () => import('../../pages/DatabaseViewSettings/DatabaseViewPermissionsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'share',
          name: RouteName.DatabaseViewLinks,
          component: () => import('../../pages/DatabaseViewSettings/DatabaseViewLinksPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'activity',
          name: RouteName.DatabaseViewActivityLog,
          component: () => import('../../pages/DatabaseViewSettings/DatabaseViewActivityLogPage.vue'),
          meta: {
            order: 1,
          },
        },
      ],
    },
    {
      path: '/@:workspaceName/database/:databaseId/s/:databaseSliceId',
      component: () => import('../../views/DatabaseSliceSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: 'settings',
          name: RouteName.DatabaseSliceSettings,
          component: () => import('../../pages/DatabaseSliceSettings/DatabaseSliceSettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: 'permissions',
          name: RouteName.DatabaseSlicePermissions,
          component: () => import('../../pages/DatabaseSliceSettings/DatabaseSlicePermissionsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'share',
          name: RouteName.DatabaseSliceLinks,
          component: () => import('../../pages/DatabaseSliceSettings/DatabaseSliceLinksPage.vue'),
          meta: {
            order: 2,
          },
        },
        {
          path: 'activity',
          name: RouteName.DatabaseSliceActivityLog,
          component: () => import('../../pages/DatabaseSliceSettings/DatabaseSliceActivityLogPage.vue'),
          meta: {
            order: 3,
          },
        },
      ],
    },
    {
      path: '/@:workspaceName/database/:databaseId/property/:propertyId/settings',
      component: () => import('../../views/DatabasePropertySettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.DatabasePropertySettings,
          component: () => import('../../pages/DatabasePropertySettings/DatabasePropertySettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: 'permissions',
          name: RouteName.DatabasePropertyPermissions,
          component: () => import('../../pages/DatabasePropertySettings/DatabasePropertyPermissionsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'webhooks',
          name: RouteName.DatabasePropertyWebhooks,
          component: () => import('../../pages/DatabasePropertySettings/DatabasePropertyWebhooksPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'activity',
          name: RouteName.DatabasePropertyActivityLog,
          component: () => import('../../pages/DatabasePropertySettings/DatabasePropertyActivityLogPage.vue'),
          meta: {
            order: 1,
          },
        },
      ],
    },
    {
      path: '/@:workspaceName/billing/invoices/:invoiceId/file',
      component: () => import('../../views/FilePreviewView.vue'),
      meta: {
        requireWorkspace: true,
        auth: {
          required: true,
        },
        depth: 150,
        isStackable: true,
        isFilePreview: true,
      },
      children: [
        {
          path: '',
          name: RouteName.WorkspaceInvoiceFile,
          component: () => import('../../pages/WorkspaceInvoiceFilePage/WorkspaceInvoiceFilePage.vue'),
        },
      ],
    },
    {
      path: '/@:workspaceName/settings',
      component: () => import('../../views/WorkspaceSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.WorkspaceSettings,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceSettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: 'permissions',
          name: RouteName.WorkspacePermissions,
          component: () => import('../../pages/WorkspaceSettings/WorkspacePermissionsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'webhooks',
          name: RouteName.WorkspaceWebhooks,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceWebhooksPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'invitations',
          name: RouteName.WorkspaceInvitations,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceInvitationsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'groups',
          name: RouteName.WorkspaceGroups,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceGroupsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'members',
          name: RouteName.WorkspaceMembers,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceMembers/WorkspaceMembersPage.vue'),
          meta: {
            order: 2,
          },
        },
        {
          path: 'activity',
          name: RouteName.WorkspaceActivityLog,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceActivityLogPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'trash',
          name: RouteName.WorkspaceTrash,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceTrashPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'export-template',
          name: RouteName.WorkspaceExportTemplate,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceExportTemplatePage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'notifications',
          name: RouteName.WorkspaceNotificationSettings,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceNotificationSettingsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'watchers',
          name: RouteName.WorkspaceWatcherSettings,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceWatcherSettingsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'usage',
          name: RouteName.WorkspaceUsage,
          redirect: {name: RouteName.WorkspaceBillingUsage},
          meta: {
            order: 1,
          },
        },
        {
          path: 'billing/usage',
          name: RouteName.WorkspaceBillingUsage,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceUsagePage.vue'),
          meta: {
            requireBilling: true,
            order: 1,
          },
        },
        {
          path: 'billing/budgets-and-alerts',
          name: RouteName.WorkspaceBillingBudgetsAndAlerts,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceBillingBudgetsAndAlertsPage.vue'),
          meta: {
            requireBilling: true,
            order: 1,
          },
        },
        {
          path: 'installed-apps',
          name: RouteName.WorkspaceInstalledApps,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceInstalledAppsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'billing/invoices',
          name: RouteName.WorkspaceBillingInvoices,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceBillingInvoicesPage.vue'),
          meta: {
            requireBilling: true,
            order: 1,
          },
        },
        {
          path: 'billing/payments',
          name: RouteName.WorkspaceBillingPayments,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceBillingPaymentsPage.vue'),
          meta: {
            requireBilling: true,
            order: 1,
          },
        },
        {
          path: 'billing/payment-methods',
          name: RouteName.WorkspaceBillingPaymentMethods,
          component: () => import('../../pages/WorkspaceSettings/WorkspaceBillingPaymentMethodsPage.vue'),
          meta: {
            requireBilling: true,
            order: 1,
          },
        },
        {
          path: 'permission-requests',
          name: RouteName.WorkspacePermissionRequests,
          component: () => import('../../pages/WorkspaceSettings/WorkspacePermissionRequestsPage.vue'),
          meta: {
            order: 1,
          },
        },
      ],
    },
    {
      path: '/@:workspaceName/upgrade',
      name: RouteName.WorkspaceUpgradePage,
      component: () => import('../../pages/WorkspaceSettings/WorkspaceUpgradePage.vue'),
      meta: {
        requireBilling: true,
        routeLayer: RouteLayerType.Billing,
        depth: 200,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
    },
    {
      path: '/@:workspaceName/checkout',
      name: RouteName.WorkspaceCheckout,
      component: () => import('../../pages/WorkspaceSettings/CheckoutPage.vue'),
      meta: {
        requireBilling: true,
        routeLayer: RouteLayerType.Billing,
        depth: 200,
        requireWorkspace: true,
        auth: {
          required: true,
        },
      },
    },
    {
      path: '/settings',
      component: () => import('../../views/UserSettingsView.vue'),
      meta: {
        routeLayer: RouteLayerType.Settings,
        depth: 150,
        auth: {
          required: true,
        },
      },
      children: [
        {
          path: '',
          name: RouteName.Settings,
          component: () => import('../../pages/UserSettings/UserSettingsPage/UserSettingsPage.vue'),
          meta: {
            order: 0,
          },
        },
        {
          path: 'appearance',
          name: RouteName.AppearanceSettings,
          component: () => import('../../pages/UserSettings/AppearanceSettingsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'desktop-app',
          name: RouteName.DesktopAppSettings,
          component: () => import('../../pages/UserSettings/DesktopAppSettingsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'colors-comparison',
          name: RouteName.ColorsComparison,
          component: () => import('../../pages/ColorsComparisonPage.vue'),
          meta: {
            order: 999,
          },
        },
        {
          path: 'security',
          name: RouteName.AccountSecuritySettings,
          component: () => import('../../pages/UserSettings/AccountSecuritySettingsPage.vue'),
          meta: {
            order: 2,
          },
        },
        {
          path: 'api-keys',
          name: RouteName.ApiKeys,
          component: () => import('../../pages/UserSettings/ApiKeysSettingsPage.vue'),
          meta: {
            order: 3,
          },
        },
        {
          path: 'experiments',
          name: RouteName.UserExperimentalFlagSettings,
          component: () => import('../../pages/UserSettings/UserExperimentalFlagsPage/UserExperimentalFlagsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'ai-tool-permissions',
          name: RouteName.AiToolPermissionsSettings,
          component: () => import('../../pages/UserSettings/AiToolPermissionsPage/AiToolPermissionsPage.vue'),
          meta: {
            order: 1,
          },
        },
        {
          path: 'sessions',
          name: RouteName.UserSessions,
          component: () => import('../../pages/UserSettings/UserSessionsPage.vue'),
          meta: {
            order: 4,
          },
        },
        {
          path: 'time-tracker',
          name: RouteName.MyTimeTrackerSettings,
          component: () => import('../../pages/UserSettings/MyTimeTrackerSettingsPage.vue'),
          meta: {
            order: 4,
          },
        },
        {
          path: 'authorized-apps',
          name: RouteName.UserAuthorizedApps,
          component: () => import('../../pages/UserSettings/UserAuthorizedAppsPage.vue'),
          meta: {
            order: 5,
          },
        },
        {
          path: 'permission-requests',
          name: RouteName.UserPermissionRequests,
          component: () => import('../../pages/UserSettings/UserPermissionRequestsPage.vue'),
          meta: {
            order: 5,
          },
        },
        {
          path: 'notifications',
          name: RouteName.NotificationsSettings,
          component: () => import('../../pages/UserSettings/UserNotificationsSettingsPage.vue'),
          meta: {
            order: 6,
          },
        },
        {
          path: 'developer',
          name: RouteName.AdminDeveloperSettings,
          component: () => import('../../pages/UserSettings/AdminDeveloperSettingsPage.vue'),
          meta: {
            order: 6,
            requireSuperAdmin: true,
          },
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      component: () => import('../../views/LandingView.vue'),
      meta: {
        depth: 150,
      },
      children: [
        {
          path: '',
          name: RouteName.NotFound,
          component: () => import('../../pages/NotFoundPage/NotFoundPage.vue'),
          meta: {
            order: 0,
          },
        },
      ],
    },
  ])
}
